import { Star, Quote, MessageCircle } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { REVIEWS, waLink } from "@/data/site";

const Stars = () => (
  <div className="flex gap-1 text-gold" aria-label="5 out of 5 stars">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} size={14} fill="currentColor" />
    ))}
  </div>
);

export default function Reviews() {
  return (
    <>
      <Seo
        title="Spa Reviews & Guest Testimonials | Ody Luxurious Spa, Manikonda"
        description="Five-star spa reviews for Ody Luxurious Spa Manikonda — guests on our couple massages, 24K gold facials, foot reflexology and private jacuzzi experiences."
        keywords="spa reviews Manikonda, best spa reviews Hyderabad, Ody Luxurious Spa reviews, couple spa testimonials"
      />
      <PageHero
        eyebrow="Guest Words"
        title="Five stars, whispered softly"
        subtitle="Unfiltered words from guests who walked in tired and floated out. Yours could be the next story here."
        crumbs={[{ label: "Reviews" }]}
      />
      <section className="py-16 sm:py-20 bg-alabaster">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div
              data-testid="reviews-summary"
              className="flex flex-wrap items-center gap-8 rounded-3xl border border-bronze/10 bg-cream p-8 sm:p-10"
            >
              <div className="text-center">
                <p className="font-serif text-6xl text-ink">5.0</p>
                <div className="mt-2 flex justify-center">
                  <Stars />
                </div>
                <p className="mt-2 text-xs uppercase tracking-[0.2em] text-stone2">Guest rating</p>
              </div>
              <div className="gold-rule w-px self-stretch hidden sm:block" style={{ width: 1 }} />
              <div className="flex-1 min-w-[240px]">
                <p className="font-serif text-xl sm:text-2xl text-ink leading-snug">
                  “The most calming ninety minutes I've had in years. Manikonda finally has a
                  proper luxury spa.”
                </p>
                <p className="mt-3 text-[0.7rem] uppercase tracking-[0.22em] text-bronze">
                  Ananya R. — Swedish Massage
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {REVIEWS.map((r, i) => (
              <Reveal key={r.name} delay={(i % 3) * 0.08}>
                <figure
                  data-testid={`review-card-${i + 1}`}
                  className="h-full rounded-2xl border border-bronze/10 bg-cream p-7 transition-[transform,border-color] duration-500 hover:-translate-y-1.5 hover:border-bronze/30"
                >
                  <div className="flex items-center justify-between">
                    <Stars />
                    <Quote size={18} className="text-gold/50" />
                  </div>
                  <blockquote className="mt-5 text-sm leading-relaxed text-stone2">
                    “{r.text}”
                  </blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-bronze/10">
                    <p className="font-serif text-lg text-ink">{r.name}</p>
                    <p className="text-[0.65rem] uppercase tracking-[0.2em] text-bronze mt-1">
                      {r.service}
                    </p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-16 text-center">
              <p className="text-sm text-stone2 mb-6">
                Had a session with us? We would love to hear about it.
              </p>
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="reviews-share-btn"
                className="inline-flex items-center gap-3 rounded-full bg-wa text-white px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-wa-dark"
              >
                <MessageCircle size={15} /> Share your experience
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
